import { DefaultPropertyFilter, UniversalLocationNode } from 'types'
import { normaliceName } from './helper.rendering'

/**
 * Recorre recursivamente el árbol de ubicaciones y arma el array con los que coinciden con checked.
 */ type FiltersLocations = { id: string; name: string; type?: string }

export const getCheckedLocationNames = (
  checked: string[],
  locations: UniversalLocationNode[],
): FiltersLocations[] => {
  const result: FiltersLocations[] = []

  // const getLastPart = (id: string) => id.split('>').pop()?.trim() || id // Separa en caso de que el ID sea name (caso abm)
  const getLastPart = (id: string) => id.split('>').pop()?.trim() || id // Separa en caso de que el ID sea name (caso abm)

  const traverse = (nodes: UniversalLocationNode[]) => {
    for (const node of nodes) {
      const nodeId = node.id || node.name

      if (checked.includes(nodeId)) {
        result.push({ id: getLastPart(nodeId), name: node.name, type: 'neighborhood' })
      }

      if (node.children?.length) {
        traverse(node.children)
      }
    }
  }

  traverse(locations)
  return result
}

// Función para obtener los filtros activos agrupados

export function checkActiveChildrensByNode(
  activeFilters: FiltersLocations[], // Filtros activos
  node: UniversalLocationNode, // Árbol de ubicaciones completo
): boolean {
  return false
}

export function getActiveFiltersSummary(
  activeFilters: FiltersLocations[], // Filtros activos
  nodes: UniversalLocationNode[], // Árbol de ubicaciones completo
): FiltersLocations[] {
  const summaries: FiltersLocations[] = []

  return activeFilters
}

export function findNodeByName(
  node: UniversalLocationNode,
  name: string,
): UniversalLocationNode | null {
  if (normaliceName(node.name.toLowerCase()) === normaliceName(name.toLowerCase())) {
    return node
  }

  for (const child of node.children) {
    const result = findNodeByName(child, name)
    if (result) return result
  }

  return null
}

function collectAllDescendants(node: UniversalLocationNode): { id: string; name: string }[] {
  let result = [{ id: node.id, name: node.name }]

  for (const child of node.children) {
    result = result.concat(collectAllDescendants(child))
  }

  return result
}

export function getAllChildrenByName(
  tree: UniversalLocationNode[],
  name: string,
): { id: string; name: string }[] {
  for (const node of tree) {
    const found = findNodeByName(node, name)
    if (found) return collectAllDescendants(found)
  }

  return [] // Si no se encuentra el nombre
}

export function findNodeByNameInTree(
  node: UniversalLocationNode,
  name: string,
): UniversalLocationNode | null {
  if (normaliceName(node.name.toLowerCase()) === normaliceName(name.toLowerCase())) {
    return node
  }

  for (const child of node.children) {
    const result = findNodeByNameInTree(child, normaliceName(name))
    if (result) return result
  }

  return null
}

export function findNodeByNameInTreeGlobal(
  nodes: UniversalLocationNode[],
  name: string,
): UniversalLocationNode | null {
  for (const node of nodes) {
    const res = findNodeByNameInTree(node, name)
    if (res) {
      return res
    }
  }
  return null
}

// Resumen de filtros agrupados para activos.
export function obtenerResumenFiltrosActivos(
  locationsTree: UniversalLocationNode[],
  filtrosActivos: DefaultPropertyFilter[],
): string[] {
  let resumen: string[] = []

  const filtrosActivosIds = filtrosActivos.map((filtro) => filtro.id?.toString()) //Armo ids de los activos.

  function verificarNodo(nodo: UniversalLocationNode) {
    if (!nodo.children || nodo.children.length === 0) {
      // Si el nodo no tiene hijos, verificamos si su ID está en los filtros activos
      if (filtrosActivosIds.includes(nodo.id)) {
        resumen.push(nodo.name)
      }
      return
    }

    // Verificamos si todos los hijos están activos
    const hijosActivos = nodo.children.filter((hijo) => filtrosActivosIds.includes(hijo.id)) // Estos son los hijos del nodo actual.

    if (hijosActivos.length === nodo.children.length) {
      // Si todos los hijos están activos, agregamos el nombre del padre al resumen
      resumen.push(nodo.name)
    } else {
      // Si no todos los hijos están activos, o si no hay hijos, recorremos los hijos
      nodo.children.forEach(verificarNodo)
    }
  }

  // Iteramos sobre la raíz del árbol
  locationsTree.forEach(verificarNodo)

  //En este punto armamos los ultimos, ahora vamos hacía arriba.

  function verificarSuperior(nodo: string) {
    //Nodo es el string nombre de la ubicación, voy a buscarla entre todos
    const superior = encontrarPadrePorNombreDeHijo(locationsTree, nodo) //Es el padre del nodoActual.
    if (superior) {
      //Me fijo cuantos activos tiene este nodo actual.
      //Cuento cuantos activos tiene el padre.
      const activos_from_superior = contarActivosEnNodoPadre(superior, resumen)
      if (activos_from_superior == superior.children.length) {
        //Si coinciden los hijos activos, con los hijos..
        if (encontrarPadrePorNombreDeHijo(locationsTree, superior.name)) {
          resumen.push(superior.name) //Agrego el "padre"
          const purge = superior.children?.map((str) => str.name) ?? [] //Agrupo los hijos
          resumen = resumen.filter((str) => !purge.includes(str)) //Los saco.
        } else {
          resumen.push(nodo)
        }
      }
    }
  }

  resumen.forEach(verificarSuperior) //Esto valida hasta la provincia.

  return Array.from(new Set(resumen)) // Devolvemos un array con nombres únicos
}

function encontrarPadrePorNombreDeHijo(
  locationsTree: UniversalLocationNode[],
  nombreHijo: string,
): UniversalLocationNode | undefined {
  for (const nodo of locationsTree) {
    if (nodo.children?.some((hijo) => hijo.name === nombreHijo)) {
      return nodo
    }
    const padreEnSubarbol = encontrarPadrePorNombreDeHijo(nodo.children || [], nombreHijo)
    if (padreEnSubarbol) {
      return padreEnSubarbol
    }
  }
  return undefined
}

export function contarActivosEnNodoPadre(
  nodoPadre: UniversalLocationNode,
  nombresActivos: string[],
): number {
  let contador = 0

  function recorrerSubarbol(nodo: UniversalLocationNode) {
    if (nombresActivos.includes(nodo.name)) {
      contador++
    }
    nodo.children?.forEach(recorrerSubarbol)
  }

  recorrerSubarbol(nodoPadre)
  return contador
}

export function unselectFiltersByNode(
  nodos: UniversalLocationNode[] | null,
  activeFilters: string[],
): string[] {
  const activosFiltrados = [...activeFilters] // Creamos una copia para no modificar el original
  function recorrerDescendientes(nodoActual: UniversalLocationNode) {
    if (activosFiltrados.includes(nodoActual.id)) {
      const index = activosFiltrados.indexOf(nodoActual.id)
      activosFiltrados.splice(index, 1)
    }
    nodoActual.children?.forEach(recorrerDescendientes)
  }

  if (nodos) nodos.forEach(recorrerDescendientes)
  return activosFiltrados
}

export function deseleccionarDescendientesActivosPorId(
  nodo: UniversalLocationNode | null,
  filtrosActivos: DefaultPropertyFilter[],
): DefaultPropertyFilter[] {
  const activosFiltrados = [...filtrosActivos] // Creamos una copia para no modificar el original

  function recorrerDescendientes(nodoActual: UniversalLocationNode) {
    const indexParaEliminar = activosFiltrados.findIndex(
      (filtro) => filtro.id?.toString() === nodoActual.id?.toString(),
    )
    if (indexParaEliminar !== -1) {
      activosFiltrados.splice(indexParaEliminar, 1)
      // Si el nodo actual fue deseleccionado, no necesitamos seguir buscando en sus hijos
      return
    }
    nodoActual.children?.forEach(recorrerDescendientes)
  }

  if (nodo) {
    recorrerDescendientes(nodo)
  }

  return activosFiltrados
}

export function deseleccionarDescendientesActivosPorName( //Devuelve el array de strings.
  nodo: UniversalLocationNode | null,
  filtrosActivos: DefaultPropertyFilter[],
): DefaultPropertyFilter[] {
  const activosFiltrados = [...filtrosActivos] // Creamos una copia para no modificar el original

  function recorrerDescendientes(nodoActual: UniversalLocationNode) {
    const indexParaEliminar = activosFiltrados.findIndex(
      (filtro) => filtro.id?.toString() === nodoActual.id?.toString(),
    )
    if (indexParaEliminar !== -1) {
      activosFiltrados.splice(indexParaEliminar, 1)
      // Si el nodo actual fue deseleccionado, no necesitamos seguir buscando en sus hijos
      return
    }
    nodoActual.children?.forEach(recorrerDescendientes)
  }

  if (nodo) {
    recorrerDescendientes(nodo)
  }
  return activosFiltrados
}
